/**
 * SQL Server's Encrypt / TrustServerCertificate values, read one way for a pasted connection string
 * (`connection-string/parseAdoNet.ts`, `parseJdbcSqlServer.ts`) and every import source. The importers (SSMS, Azure
 * Data Studio, VS Code, ODBC DSNs, DBeaver, DataGrip) read them with the twin of this rule in
 * `electron/connection-import/shared/mssqlImport.ts` — a copy, because that module keeps `src/` out of the main
 * process's type program — and `src/test/coupling/mssqlEncryptPasteImportCoupling.test.ts` pins the two value by value.
 *
 * Values: true / yes / 1 / on / require(d) / mandatory are on; false / no / 0 / off / optional / disable(d) are off. A
 * source that names neither keyword leaves the setting to Jam's defaults (Encrypt on, Trust off); one that states any
 * other value (or a blank one) is refused on paste and skipped on import (`unreadableMssqlTls`, review round 5).
 *
 * `Encrypt=Strict` is TDS 8.0 strict encryption, which always validates the server certificate. Each driver's own page:
 * - Microsoft.Data.SqlClient 5+, "SqlConnection.ConnectionString" (the Encrypt keyword): "When `strict`, TDS 8.0 TLS
 *   encryption is used and the `TrustServerCertificate` setting is ignored and treated as false"; its "Connection string
 *   syntax" page: "this setting is ignored when `Encrypt` is set to `Strict`. The server certificate is always validated
 *   in `Strict` mode."
 * - ODBC Driver 18, "DSN and Connection String Keywords" (Encrypt): "`TrustServerCertificate` is ignored. Server
 *   certificate is checked."
 * - mssql-jdbc, "Understanding encryption support": "The trustServerCertificate setting is ignored and treated as false
 *   in `strict` mode."
 * So a strict source is read as Encrypt on AND Trust server certificate off, whatever it says about trust — a
 * `TrustServerCertificate=yes` next to it used to come across and turn certificate validation off (review round 3, P0).
 *
 * Jam stores Encrypt as on / off, and both of its SQL Server paths (tedious, SQL Tools Service) are given that boolean,
 * so the TDS 8.0 strict handshake itself is not used — `ENCRYPT_STRICT_NOTE` says so wherever a strict source is shown.
 */

export type MssqlTlsSettings = {
  encrypt?: boolean;
  trustServerCertificate?: boolean;
  /** The source said `Encrypt=Strict`: encrypted, certificate validated (Trust forced off). */
  encryptStrict?: true;
};

/** What the paste preview and the import review say about a strict source. */
export const ENCRYPT_STRICT_NOTE =
  'Encrypt=Strict: encrypted with certificate validation, so Trust server certificate is off (TDS 8.0 strict mode is not used).';

/** The on / off spellings — the import copy's sets must be the same, set to set (the coupling test compares them). */
export const MSSQL_TLS_ON: ReadonlySet<string> = new Set(['true', 'yes', '1', 'on', 'require', 'required', 'strict', 'mandatory']);
export const MSSQL_TLS_OFF: ReadonlySet<string> = new Set(['false', 'no', '0', 'off', 'optional', 'disable', 'disabled']);

const lowered = (value: unknown): string => (value != null ? String(value).trim().toLowerCase() : '');

/** `Encrypt=Strict`, in any case and spacing. */
export function isStrictEncrypt(value: unknown): boolean {
  return typeof value === 'string' && lowered(value) === 'strict';
}

/** On / off as SQL Server's drivers read an Encrypt or TrustServerCertificate value; `strict` is on. */
export function mssqlBoolish(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  const v = lowered(value);
  if (MSSQL_TLS_ON.has(v)) return true;
  if (MSSQL_TLS_OFF.has(v)) return false;
  return undefined;
}

/** Encrypt / Trust server certificate from a source's two values: only what it states, and strict forces Trust off. */
export function mssqlTlsSettings(encrypt: unknown, trustServerCertificate: unknown): MssqlTlsSettings {
  if (isStrictEncrypt(encrypt)) return { encrypt: true, trustServerCertificate: false, encryptStrict: true };
  const out: MssqlTlsSettings = {};
  const e = mssqlBoolish(encrypt);
  const t = mssqlBoolish(trustServerCertificate);
  if (e !== undefined) out.encrypt = e;
  if (t !== undefined) out.trustServerCertificate = t;
  return out;
}

/** The two TLS keywords, as a refused paste or a skipped import row names them. */
export type MssqlTlsKeyword = 'Encrypt' | 'TrustServerCertificate';

/**
 * The keyword whose STATED value — the one the source's driver uses, after its repeated-keyword rule — is none of the
 * on / off spellings (`Encrypt=maybe`, a blank `Encrypt=`), else undefined; `null` / `undefined` is not stated. SqlClient
 * rejects an invalid value ("Connection String Builders"), so a pasted string naming one is refused and an import source
 * skipped (review round 5, P0): read as "not stated", it let a paste keep a draft's Encrypt off / Trust on, and an import
 * come across with Jam's defaults instead of what the source said. (PostgreSQL / MySQL need no such rule: any stated SSL
 * value other than an off spelling fills SSL on, so no earlier setting survives — `Docs/ConnectionDialog.md`.)
 */
export function unreadableMssqlTls(encrypt: unknown, trustServerCertificate: unknown): MssqlTlsKeyword | undefined {
  const unreadable = (value: unknown) => value != null && mssqlBoolish(value) === undefined;
  if (unreadable(encrypt)) return 'Encrypt';
  return unreadable(trustServerCertificate) ? 'TrustServerCertificate' : undefined;
}
